/**
 * Financial Management integration — shared, pure model (no I/O). Types + deterministic helpers used by
 * both the server (Plaid sync, categorization, recurring detection, the dashboard API) and the client
 * (the `#/finances` reader). Money is always integer **cents**; the sign follows Plaid's convention:
 * **positive = outflow (spending / money leaving the account), negative = inflow (income / refund).**
 *
 * See `docs/plans/financial-management-integration.md` (spec) and `financial-management-decision-log.md`
 * (DL-03 sign convention, DL-05 recurring, DL-07 lazy categorization).
 */

// --- Configuration -----------------------------------------------------------------------------

/**
 * Per-user Financial Management settings. `enabled` gates the nightly sync fan-out; the rest tune
 * alerting. Plaid credentials live in the encrypted item store, never here.
 */
export interface FinancialConfig {
  enabled: boolean;
  /** Flag a single transaction as notable when its outflow meets/exceeds this many cents (default $500). */
  largeTxnThresholdCents: number;
  /** When true, notable events also post a note to today's journal (opt-in — writes money data to notes). */
  alertToJournal: boolean;
  /**
   * NEXUS-04: when true, the AI assistant may READ financial data (balances, transactions, recurring)
   * via its finance tools. Off by default — financial-grade PII stays out of the agent until the user
   * flips this single toggle on the Financial connect panel. Never enables writes.
   */
  assistantReads?: boolean;
  /** Per-account owner label (accountId → owner code, e.g. "ER"/"CR"/"Shared") for live transactions. */
  accountOwners: Record<string, string>;
  /** Per-account display customization (friendly alias, short code, sort order) keyed by accountId. */
  accountMeta?: Record<string, AccountMeta>;
  /**
   * Friendly names for cardholders (Amex-style sub-card holder names), keyed by the raw Plaid
   * `account_owner` string, e.g. `"CYNTHIA RUFF 1013"` → `"Cynthia Ruff"`. Applied at read time to the
   * transaction's `cardholder` and its derived owner marker.
   */
  cardholderAliases?: Record<string, string>;
  /** Summary of the most recent Monarch CSV import (for a persistent reference in Settings). */
  lastMonarchImport?: MonarchImportSummary;
}

/** User-set presentation overrides for one account (all optional; the raw Plaid name is the fallback). */
export interface AccountMeta {
  /** Friendly full name shown in the Accounts list (e.g. "Joint Checking"). Falls back to the bank name. */
  alias?: string;
  /** Compact code shown in the transaction feed's Account column (e.g. "MERCO"). Falls back to the mask. */
  shortName?: string;
  /** Sort position in the Accounts list (ascending). Unset accounts sort after, by balance. */
  order?: number;
}

/** A persisted record of the last Monarch import — surfaced read-only in the Financial settings panel. */
export interface MonarchImportSummary {
  fileName: string;
  importedAt: number;
  parsed: number;
  matched: number;
  backApplied: number;
  rulesCreated: number;
  categoriesSeeded: number;
}

export function defaultFinancialConfig(): FinancialConfig {
  return { enabled: false, largeTxnThresholdCents: 50_000, alertToJournal: false, accountOwners: {}, accountMeta: {} };
}

/** Derive an owner marker from a full name: "Evan Ruff" → "ER"; "Shared"/blank pass through/→ "Shared". */
export function ownerMarker(name: string | undefined | null): string {
  const s = (name ?? '').trim();
  if (!s || /^shared$/i.test(s)) return 'Shared';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

/** A per-person spending rollup. */
export interface OwnerSummary {
  owner: string;
  spentCents: number;
  count: number;
}

/** Roll spending up by owner (outflows only; income/transfers excluded). `resolve` maps a txn → owner. */
/**
 * Spend grouped by "who". `windowDays` (when set) restricts to the trailing N days anchored on `today`
 * (default: the latest transaction date) — so "Who spent what" reads as this-month spend rather than the
 * whole multi-year backfill, matching the {@link spendByBucketWindow} card above it.
 */
export function rollupByOwner(
  txns: FinancialTransaction[],
  resolve: (t: FinancialTransaction) => string,
  opts: { today?: string; windowDays?: number } = {},
): OwnerSummary[] {
  const to = opts.today ?? txns.reduce((max, t) => (t.date > max ? t.date : max), '0000-00-00');
  // Guard the sentinel (no dated transactions): isoMinusDays on it yields an invalid date that throws.
  const from = opts.windowDays != null && to !== '0000-00-00' ? isoMinusDays(to, opts.windowDays) : null;
  const map = new Map<string, OwnerSummary>();
  for (const t of txns) {
    if (!isSpend(t)) continue;
    if (from !== null && !(t.date > from && t.date <= to)) continue;
    const owner = resolve(t) || 'Shared';
    const cur = map.get(owner) ?? { owner, spentCents: 0, count: 0 };
    cur.spentCents += t.amountCents;
    cur.count += 1;
    map.set(owner, cur);
  }
  return [...map.values()].sort((a, b) => b.spentCents - a.spentCents);
}

/** A bucket with its nested subcategories (for the grouped category picker). */
export interface CategoryGroup {
  bucket: FinancialBucket;
  categories: string[];
}

/** Group a flat (category → bucket) list into buckets with sorted subcategories, in bucket order. */
export function groupCategoriesByBucket(items: Array<{ category: string; bucket: string }>): CategoryGroup[] {
  const byBucket = new Map<string, Set<string>>();
  for (const { category, bucket } of items) {
    const b = (isKnownCategory(bucket) ? bucket : bucketForCategory(category)) as FinancialBucket;
    const set = byBucket.get(b) ?? new Set<string>();
    set.add(category);
    byBucket.set(b, set);
  }
  return FINANCIAL_CATEGORIES.filter((b) => byBucket.has(b)).map((b) => ({
    bucket: b,
    categories: [...byBucket.get(b)!].sort((a, b2) => a.localeCompare(b2)),
  }));
}

/** Coerce an untrusted PUT body into a valid {@link FinancialConfig}, filling gaps from the defaults. */
export function coerceFinancialConfig(input: unknown): FinancialConfig {
  const d = defaultFinancialConfig();
  if (!input || typeof input !== 'object') return d;
  const o = input as Record<string, unknown>;
  const num = (v: unknown, fb: number, min: number, max: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fb;
  const accountOwners: Record<string, string> = {};
  if (o.accountOwners && typeof o.accountOwners === 'object') {
    for (const [k, v] of Object.entries(o.accountOwners as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) accountOwners[k] = v.trim().slice(0, 20);
    }
  }
  const accountMeta: Record<string, AccountMeta> = {};
  if (o.accountMeta && typeof o.accountMeta === 'object') {
    for (const [k, v] of Object.entries(o.accountMeta as Record<string, unknown>)) {
      if (!v || typeof v !== 'object') continue;
      const m = v as Record<string, unknown>;
      const meta: AccountMeta = {};
      if (typeof m.alias === 'string' && m.alias.trim()) meta.alias = m.alias.trim().slice(0, 60);
      if (typeof m.shortName === 'string' && m.shortName.trim()) meta.shortName = m.shortName.trim().slice(0, 16);
      if (typeof m.order === 'number' && Number.isFinite(m.order)) meta.order = Math.round(m.order);
      if (meta.alias || meta.shortName || meta.order != null) accountMeta[k] = meta;
    }
  }
  const cardholderAliases: Record<string, string> = {};
  if (o.cardholderAliases && typeof o.cardholderAliases === 'object') {
    for (const [k, v] of Object.entries(o.cardholderAliases as Record<string, unknown>)) {
      if (k.trim() && typeof v === 'string' && v.trim()) cardholderAliases[k] = v.trim().slice(0, 60);
    }
  }
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : d.enabled,
    largeTxnThresholdCents: num(o.largeTxnThresholdCents, d.largeTxnThresholdCents, 100, 100_000_00),
    alertToJournal: typeof o.alertToJournal === 'boolean' ? o.alertToJournal : d.alertToJournal,
    // NEXUS-04: assistant-reads opt-in (default off). Preserve an explicit boolean through a PUT.
    ...(typeof o.assistantReads === 'boolean' ? { assistantReads: o.assistantReads } : {}),
    accountOwners,
    accountMeta,
    cardholderAliases,
    // Read-only server-owned field: preserve it through a client PUT so a Save never wipes it.
    ...(coerceMonarchSummary(o.lastMonarchImport) ? { lastMonarchImport: coerceMonarchSummary(o.lastMonarchImport)! } : {}),
  };
}

/** Validate a possibly-untrusted Monarch import summary (from a round-tripped config PUT). */
function coerceMonarchSummary(input: unknown): MonarchImportSummary | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const o = input as Record<string, unknown>;
  if (typeof o.fileName !== 'string' || typeof o.importedAt !== 'number') return undefined;
  const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    fileName: o.fileName.slice(0, 200),
    importedAt: o.importedAt,
    parsed: n(o.parsed),
    matched: n(o.matched),
    backApplied: n(o.backApplied),
    rulesCreated: n(o.rulesCreated),
    categoriesSeeded: n(o.categoriesSeeded),
  };
}

/** Days of history that get the LLM normalization/override pass; older keeps the Plaid PFC baseline (DL-07). */
export const CATEGORIZE_WINDOW_DAYS = 120;

/** Months of history pulled on first connect (D16). */
export const BACKFILL_MONTHS = 24;

// --- Money helpers -----------------------------------------------------------------------------

/** Parse a decimal money string/number into integer cents. Invalid → 0. Mirrors amazon's `toCents`. */
export function toCents(amount: string | number): number {
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** Format integer cents as a decimal string (no currency symbol), e.g. 1299 → "12.99". */
export function centsToStr(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Format integer cents for display with a currency symbol, e.g. (1299,'USD') → "$12.99". */
export function formatMoney(cents: number, currency = 'USD'): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const symbol = currency === 'USD' ? '$' : '';
  const body = `${symbol}${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return currency === 'USD' ? `${sign}${body}` : `${sign}${body} ${currency}`;
}

// --- Categorization ----------------------------------------------------------------------------

/** Where a transaction's category came from — precedence low→high: plaid < llm < rule < manual. */
export type CategorySource = 'plaid' | 'llm' | 'rule' | 'manual';

/** Review lifecycle. Aggregator charges start `needs-review`; a user justification makes them `justified`. */
export type ReviewStatus = 'auto' | 'needs-review' | 'justified';

/**
 * The canonical category taxonomy (D8): Plaid's Personal Finance Category *primary* set, given
 * friendly labels. Users may add custom categories on top (stored as free strings), but these are the
 * baseline every Plaid PFC maps into.
 */
export const FINANCIAL_CATEGORIES = [
  'Income',
  'Transfer',
  'Loan Payment',
  'Bank Fees',
  'Entertainment',
  'Food & Drink',
  'Groceries',
  'Shopping',
  'Personal Care',
  'Health',
  'Services',
  'Government & Non-Profit',
  'Transportation',
  'Travel',
  'Housing & Rent',
  'Utilities',
  'Education',
  'Uncategorized',
] as const;

export type FinancialCategory = (typeof FINANCIAL_CATEGORIES)[number];

/** Map a Plaid PFC `primary` (e.g. "FOOD_AND_DRINK") to a friendly {@link FinancialCategory}. */
export function categoryFromPlaidPfc(primary: string | undefined | null): FinancialCategory {
  switch ((primary ?? '').toUpperCase()) {
    case 'INCOME':
      return 'Income';
    case 'TRANSFER_IN':
    case 'TRANSFER_OUT':
      return 'Transfer';
    case 'LOAN_PAYMENTS':
      return 'Loan Payment';
    case 'BANK_FEES':
      return 'Bank Fees';
    case 'ENTERTAINMENT':
      return 'Entertainment';
    case 'FOOD_AND_DRINK':
      return 'Food & Drink';
    case 'GENERAL_MERCHANDISE':
      return 'Shopping';
    case 'PERSONAL_CARE':
      return 'Personal Care';
    case 'MEDICAL':
      return 'Health';
    case 'GENERAL_SERVICES':
      return 'Services';
    case 'GOVERNMENT_AND_NON_PROFIT':
      return 'Government & Non-Profit';
    case 'TRANSPORTATION':
      return 'Transportation';
    case 'TRAVEL':
      return 'Travel';
    case 'RENT_AND_UTILITIES':
      return 'Utilities';
    case 'HOME_IMPROVEMENT':
      return 'Housing & Rent';
    default:
      return 'Uncategorized';
  }
}

/** Whether `value` is one of the known baseline categories (custom strings also allowed elsewhere). */
export function isKnownCategory(value: string): value is FinancialCategory {
  return (FINANCIAL_CATEGORIES as readonly string[]).includes(value);
}

/**
 * Two-level taxonomy (EN-5): the 18 {@link FINANCIAL_CATEGORIES} are TOP-LEVEL *buckets*; a
 * transaction's fine-grained `category` (from Monarch or Plaid, e.g. "Water", "Child Education") is a
 * *subcategory* nested under a bucket. Charts roll up to the bucket; the feed shows the subcategory.
 */
export type FinancialBucket = FinancialCategory;

/** Keyword → bucket rules for mapping an arbitrary subcategory string to one of the 18 buckets. */
const BUCKET_KEYWORDS: Array<{ re: RegExp; bucket: FinancialBucket }> = [
  { re: /paycheck|payroll|salary|income|interest|dividend|deposit/i, bucket: 'Income' },
  { re: /transfer|xfer|venmo|zelle|wire/i, bucket: 'Transfer' },
  { re: /loan|mortgage|student loan/i, bucket: 'Loan Payment' },
  { re: /fee|interest charge|atm/i, bucket: 'Bank Fees' },
  { re: /rent|home|mortgage|hoa|furnish|furniture/i, bucket: 'Housing & Rent' },
  { re: /water|electric|gas bill|utilit|internet|cable|phone|trash|sewer/i, bucket: 'Utilities' },
  { re: /grocer|supermarket|whole foods|costco|trader joe/i, bucket: 'Groceries' },
  { re: /restaurant|coffee|bar|dining|food|fast food|takeout/i, bucket: 'Food & Drink' },
  { re: /entertain|streaming|movie|music|game|hobby|subscription/i, bucket: 'Entertainment' },
  { re: /shop|merchandise|amazon|clothing|electronics|retail/i, bucket: 'Shopping' },
  { re: /personal care|hair|salon|spa|gym|fitness/i, bucket: 'Personal Care' },
  { re: /medical|health|doctor|dentist|pharmacy|vision/i, bucket: 'Health' },
  { re: /education|tuition|school|child education|daycare|childcare/i, bucket: 'Education' },
  { re: /transport|gas|fuel|parking|transit|uber|lyft|auto|car/i, bucket: 'Transportation' },
  { re: /travel|flight|hotel|airbnb|vacation/i, bucket: 'Travel' },
  { re: /clean|service|repair|professional|legal|advisor/i, bucket: 'Services' },
  { re: /charit|donation|government|tax|non-?profit/i, bucket: 'Government & Non-Profit' },
];

/** Map an arbitrary subcategory (Monarch/derived) to one of the 18 buckets. Exact match wins; else keywords. */
export function bucketForCategory(subcategory: string): FinancialBucket {
  const s = (subcategory ?? '').trim();
  if (!s) return 'Uncategorized';
  if (isKnownCategory(s)) return s;
  for (const { re, bucket } of BUCKET_KEYWORDS) if (re.test(s)) return bucket;
  return 'Uncategorized';
}

/** Material Symbols icon name per bucket (feed rows + charts). */
export const BUCKET_ICONS: Record<FinancialBucket, string> = {
  Income: 'payments',
  Transfer: 'swap_horiz',
  'Loan Payment': 'account_balance',
  'Bank Fees': 'receipt_long',
  Entertainment: 'movie',
  'Food & Drink': 'restaurant',
  Groceries: 'shopping_basket',
  Shopping: 'shopping_bag',
  'Personal Care': 'self_improvement',
  Health: 'medical_services',
  Services: 'handyman',
  'Government & Non-Profit': 'account_balance',
  Transportation: 'directions_car',
  Travel: 'flight',
  'Housing & Rent': 'home',
  Utilities: 'bolt',
  Education: 'school',
  Uncategorized: 'category',
};

/** Stable display color per bucket (hex; theme-agnostic, works on light/dark). */
export const BUCKET_COLORS: Record<FinancialBucket, string> = {
  Income: '#22c55e',
  Transfer: '#64748b',
  'Loan Payment': '#7c3aed',
  'Bank Fees': '#9ca3af',
  Entertainment: '#ec4899',
  'Food & Drink': '#f97316',
  Groceries: '#84cc16',
  Shopping: '#2563eb',
  'Personal Care': '#14b8a6',
  Health: '#ef4444',
  Services: '#0ea5e9',
  'Government & Non-Profit': '#6366f1',
  Transportation: '#eab308',
  Travel: '#06b6d4',
  'Housing & Rent': '#8b5cf6',
  Utilities: '#3b82f6',
  Education: '#f59e0b',
  Uncategorized: '#94a3b8',
};

/** Icon for a bucket (falls back to the generic category icon). */
export function bucketIcon(bucket: string): string {
  return BUCKET_ICONS[bucket as FinancialBucket] ?? 'category';
}

/** Color for a bucket (falls back to a neutral gray). */
export function bucketColor(bucket: string): string {
  return BUCKET_COLORS[bucket as FinancialBucket] ?? '#94a3b8';
}

// --- Merchant normalization --------------------------------------------------------------------

/**
 * Known merchants: a raw-descriptor pattern → its canonical display name. Checked first by
 * {@link normalizeMerchant} so noisy descriptors ("AMZN Mktp US*2X4B1", "SQ *BLUE BOTTLE") collapse to
 * a stable name. Order matters — first match wins.
 */
export const KNOWN_MERCHANTS: Array<{ pattern: RegExp; name: string }> = [
  // Specific merchants first — a generic "amazon" match must not shadow "amazon prime".
  { pattern: /amazon prime|prime video|amzn ?prime|amznprime/i, name: 'Amazon Prime' },
  { pattern: /amzn|amazon\b|amazon mktp|amazon\.com/i, name: 'Amazon' },
  { pattern: /paypal/i, name: 'PayPal' },
  { pattern: /disney\s*\+|disney\s*plus|disneyplus/i, name: 'Disney+' },
  { pattern: /netflix/i, name: 'Netflix' },
  { pattern: /spotify/i, name: 'Spotify' },
  { pattern: /hulu/i, name: 'Hulu' },
  { pattern: /hbo\s*max|max\.com/i, name: 'Max' },
  { pattern: /youtube\s*premium|google\s*youtube/i, name: 'YouTube Premium' },
  { pattern: /apple\.com\/bill|apple\s*services|itunes/i, name: 'Apple' },
  { pattern: /uber\s*eats/i, name: 'Uber Eats' },
  { pattern: /uber(?!\s*eats)/i, name: 'Uber' },
  { pattern: /lyft/i, name: 'Lyft' },
  { pattern: /doordash|door\s*dash/i, name: 'DoorDash' },
  { pattern: /venmo/i, name: 'Venmo' },
  { pattern: /cash\s*app|square\s*cash/i, name: 'Cash App' },
  { pattern: /starbucks/i, name: 'Starbucks' },
  { pattern: /walmart|wal-mart/i, name: 'Walmart' },
  { pattern: /target\b/i, name: 'Target' },
  { pattern: /costco/i, name: 'Costco' },
  { pattern: /whole\s*foods|wholefds/i, name: 'Whole Foods' },
];

/**
 * Normalize a raw bank descriptor into a stable, human merchant name. Known merchants win; otherwise
 * strips POS/transaction noise, star/hash segments, store numbers, and title-cases the remainder.
 * Pure + deterministic.
 */
export function normalizeMerchant(raw: string): string {
  const input = (raw ?? '').trim();
  if (!input) return 'Unknown';
  for (const { pattern, name } of KNOWN_MERCHANTS) if (pattern.test(input)) return name;
  let s = input;
  // Drop anything after a "*" (payment-processor prefix like "TST* " or "SQ *") — keep the tail.
  if (s.includes('*')) {
    const parts = s.split('*');
    s = parts[parts.length - 1] ?? s;
  }
  s = s
    .replace(/\b(POS|PURCHASE|DEBIT|CREDIT|CARD|PAYMENT|RECURRING|ACH|WEB|ID)\b/gi, ' ')
    .replace(/#+/g, ' ')
    .replace(/\b\d{3,}\b/g, ' ') // store numbers, ids, phone fragments
    .replace(/\b[A-Z]{2}\b$/i, ' ') // trailing state code
    .replace(/[^A-Za-z0-9&'.\- ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return input.slice(0, 40);
  return s
    .split(' ')
    .map((w) => (w.length <= 3 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
    .slice(0, 60);
}

/** Stable logo key for a merchant (djb2 → base36). Shared by client + server so the proxy URL matches. */
export function logoKeyFor(merchant: string): string {
  let h = 5381;
  const s = (merchant ?? '').trim().toLowerCase();
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return 'lg_' + (h >>> 0).toString(36);
}

/** Extract a bare domain (host, no scheme/www/path) from a website string, or '' if none. */
export function domainFromWebsite(website: string | undefined | null): string {
  if (!website) return '';
  let s = website.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/^www\./, '');
  s = s.split(/[/?#]/)[0] ?? '';
  return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(s) ? s : '';
}

/** Aggregators/processors whose single charge hides the real purchase — flagged for drill-down (D6). */
export const AGGREGATOR_MERCHANTS = new Set(['Amazon', 'PayPal', 'Apple', 'Cash App', 'Venmo', 'Square', 'Google']);

/** Whether a normalized merchant is an opaque aggregator (needs receipt-level drill-down to justify). */
export function isAggregatorMerchant(normalized: string): boolean {
  return AGGREGATOR_MERCHANTS.has(normalized);
}

// --- Transactions / accounts (DTOs) -------------------------------------------------------------

/** A synced, normalized transaction. Sensitive fields are decrypted server-side before this DTO ships. */
export interface FinancialTransaction {
  txnId: string;
  accountId: string;
  /** The Plaid item (bank connection) this transaction belongs to — used for per-item purge. */
  itemId?: string;
  /** ISO date `YYYY-MM-DD`. */
  date: string;
  /** Integer cents; positive = outflow, negative = inflow (DL-03). */
  amountCents: number;
  currency: string;
  /** Raw bank descriptor (sensitive; encrypted at rest). */
  rawMerchant: string;
  /** Cleaned display name (plaintext, queryable). */
  normalizedMerchant: string;
  /** The nicest available merchant name (Monarch → Plaid → Gemini → normalized), shown in the ledger. */
  displayName?: string;
  /** Plaid merchant entity id (stable across charges) — the logo/registry key. */
  merchantEntityId?: string;
  /** Resolved same-origin logo reference (lapo:// or URL); absent → render a monogram. */
  logoUrl?: string;
  /** Plaid PFC primary, retained for audit/debug. */
  plaidPfc?: string;
  /** Fine-grained subcategory shown in the feed (Monarch/derived, e.g. "Water"). */
  category: string;
  /** One of the 18 top-level buckets (for charts/rollups). Derived from `category` when absent. */
  bucket?: string;
  categorySource: CategorySource;
  /** 0..1 confidence in the category (1 for rule/manual). */
  confidence: number;
  isAggregator: boolean;
  reviewStatus: ReviewStatus;
  recurringStreamId?: string;
  /** Owner marker (e.g. "ER"/"CR"/"Shared") — from Monarch's Owner column for imported rows. */
  owner?: string;
  /** Cardholder name on the charge, when the issuer provides it (Amex supplementary cards) — "whose card". */
  cardholder?: string;
  pending: boolean;
}

/** Depository/credit/loan/investment account with balances (sensitive balances decrypted server-side). */
export interface FinancialAccount {
  accountId: string;
  itemId: string;
  name: string;
  /** Last 4, if provided by Plaid (sensitive). */
  mask?: string;
  /** Plaid account `type`: depository | credit | loan | investment | other. */
  type: string;
  subtype?: string;
  currentBalanceCents: number;
  availableBalanceCents?: number;
  currency: string;
  /** True for assets (depository, investment); false for liabilities (credit, loan). */
  isAsset: boolean;
}

/** A connected Plaid institution ("item"). The access token lives in KMS under `secretKey`. */
export interface FinancialItem {
  itemId: string;
  userId: string;
  institutionName: string;
  secretKey: string;
  /** Plaid transactions cursor for incremental `/transactions/sync`. */
  cursor: string | null;
  status: 'active' | 'login-required' | 'error';
  lastSyncAt: number;
  createdAt: number;
}

/**
 * STENCIL — a single AND-ed predicate over one transaction field (spec §4). All of a rule's conditions
 * must match. String ops are case-insensitive unless `caseSensitive`; amount ops compare MAGNITUDE
 * (abs cents), so `$150` matches a $150 charge or refund (D4). `between` is inclusive on both bounds.
 */
export type RuleCondition =
  | { field: 'merchant'; op: 'equals' | 'contains' | 'regex'; value: string; caseSensitive?: boolean }
  | { field: 'descriptor'; op: 'equals' | 'contains' | 'regex'; value: string; caseSensitive?: boolean }
  | { field: 'account'; op: 'equals'; value: string }
  | { field: 'cardholder'; op: 'equals' | 'contains'; value: string; caseSensitive?: boolean }
  | { field: 'owner'; op: 'equals'; value: string }
  | {
      field: 'amount';
      op: 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'between';
      /** Integer cents, MAGNITUDE. Used by all ops except `between`. */
      cents?: number;
      /** `between` bounds (inclusive), integer cents magnitude. */
      minCents?: number;
      maxCents?: number;
    };

/**
 * A per-user categorization rule (STENCIL, spec §4). `conditions` are AND-ed; the highest-specificity
 * matching rule wins (spec §6). `conditions` is optional for back-compat: legacy rows carry only
 * `normalizedMerchant` and are migrated on read via `ruleConditions()` (SDL-1/D6).
 */
export interface FinancialRule {
  ruleId: string;
  /** AND-ed predicates. Absent on un-migrated legacy rows (see `normalizedMerchant`). */
  conditions?: RuleCondition[];
  category: string;
  /** Optional explicit tiebreak after specificity; higher wins. Default 0 (D5). */
  priority?: number;
  createdAt: number;
  updatedAt?: number;
  /** @deprecated legacy single-merchant matcher; kept for rollback + as the read-migration source (D6). */
  normalizedMerchant?: string;
}

// --- Recurring streams -------------------------------------------------------------------------

export type Cadence = 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';

/** Approximate period length in days for each cadence (for `nextExpected` + staleness). */
export const CADENCE_DAYS: Record<Cadence, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 91,
  yearly: 365,
};

/** A detected recurring charge stream (subscription/bill). Built by `detectRecurring`. */
export interface RecurringStream {
  streamId: string;
  normalizedMerchant: string;
  cadence: Cadence;
  /** Median charge in cents (positive outflow). */
  avgAmountCents: number;
  currency: string;
  /** The account/card that funds this stream (answers "how am I paying for X?"). */
  fundingAccountId: string;
  firstSeen: string;
  lastSeen: string;
  /** ISO date the next charge is expected (lastSeen + cadence). */
  nextExpected: string;
  status: 'active' | 'ended';
  /** When set, this stream duplicates another (same merchant, different funding) — e.g. two Prime accounts. */
  isDuplicateOf?: string;
  /** Heuristic: likely a forgotten/overlooked subscription. */
  forgottenHint: boolean;
  /** When true, the most recent charge is materially above the historical median (a quiet price hike). */
  priceIncrease: boolean;
  /** Percent the latest charge rose vs the prior median (0 when no increase). */
  priceIncreasePct: number;
  /** How many transactions back this stream. */
  sampleCount: number;
  /** Most-common fine-grained subcategory of the charges (for the category chip). */
  category?: string;
  /** Most-common top-level bucket of the charges (icon/color). */
  bucket?: string;
  /** The contributing transaction ids, newest first — to show the original charges. */
  sampleTxnIds?: string[];
  /** User-set friendly name (overrides {@link normalizedMerchant} for display). */
  displayName?: string;
  /** True when the user marked this stream "not recurring" — hidden from the list by default. */
  dismissed?: boolean;
}

/** A per-user override for a detected recurring stream (rename / mark not-recurring). Survives recompute. */
export interface RecurringOverride {
  /** Stable identity across recomputes: `normalizedMerchant::fundingAccountId` (cadence-independent). */
  key: string;
  displayName?: string;
  dismissed?: boolean;
}

/** The stable override key for a stream (independent of cadence, which can drift). */
export function recurringOverrideKey(s: Pick<RecurringStream, 'normalizedMerchant' | 'fundingAccountId'>): string {
  return `${s.normalizedMerchant}::${s.fundingAccountId}`;
}

/** Apply user overrides (rename + dismiss) onto freshly-detected streams. Pure. */
export function applyRecurringOverrides(streams: RecurringStream[], overrides: RecurringOverride[]): RecurringStream[] {
  const byKey = new Map(overrides.map((o) => [o.key, o]));
  return streams.map((s) => {
    const o = byKey.get(recurringOverrideKey(s));
    if (!o) return s;
    return { ...s, displayName: o.displayName || s.displayName, dismissed: o.dismissed ?? false };
  });
}

// --- Dashboard rollups -------------------------------------------------------------------------

export interface NetWorth {
  assetsCents: number;
  liabilitiesCents: number;
  investmentsCents: number;
  netCents: number;
  currency: string;
}

export interface CategorySummary {
  category: string;
  spentCents: number;
  count: number;
}

export interface TrendPoint {
  /** `YYYY-MM`. */
  month: string;
  spentCents: number;
}

export type AlertKind = 'large-transaction' | 'new-recurring' | 'duplicate-subscription' | 'price-increase' | 'sync-failure';
export type AlertSeverity = 'info' | 'warn' | 'error';

export interface FinancialAlert {
  alertId: string;
  kind: AlertKind;
  severity: AlertSeverity;
  detail: string;
  at: number;
  dismissed: boolean;
  /** SIGNAL-TUNING — content identity (`large|merchant|amountCents|date`) for large-charge alerts.
   * Plaid RE-ISSUES txnIds (pending→posted, re-links), so the txnId-keyed alertId alone re-raised the
   * SAME charge as a "new" alert after the owner dismissed the old one. Dedupe consults this too. */
  fingerprint?: string;
}

/** Compute net worth from accounts. Investments are counted separately AND in assets total. */
export function computeNetWorth(accounts: FinancialAccount[], currency = 'USD'): NetWorth {
  let assets = 0;
  let liabilities = 0;
  let investments = 0;
  for (const a of accounts) {
    if (a.type === 'investment') investments += a.currentBalanceCents;
    if (a.isAsset) assets += a.currentBalanceCents;
    else liabilities += a.currentBalanceCents;
  }
  return { assetsCents: assets, liabilitiesCents: liabilities, investmentsCents: investments, netCents: assets - liabilities, currency };
}

// --- Account presentation ----------------------------------------------------------------------

/** Friendly full name for the Accounts list: the user's alias, else the bank-provided name. */
export function accountDisplayName(account: Pick<FinancialAccount, 'name'>, meta?: AccountMeta): string {
  return (meta?.alias && meta.alias.trim()) || account.name;
}

/**
 * Compact label for the transaction feed's Account column: the user's short name (e.g. "MERCO"),
 * else the last-4 mask ("••2003"), else an acronym of the name. Kept short on purpose.
 */
export function accountShortName(account: Pick<FinancialAccount, 'name' | 'mask'>, meta?: AccountMeta): string {
  const short = meta?.shortName?.trim();
  if (short) return short;
  if (account.mask) return `••${account.mask}`;
  const acronym = account.name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
  return acronym || account.name.slice(0, 6);
}

/**
 * Sort accounts for display: those with an explicit `order` first (ascending), then the rest by
 * balance descending. Stable + deterministic — no clock/RNG. `meta` maps accountId → its metadata.
 */
export function sortAccountsForDisplay(accounts: FinancialAccount[], meta: Record<string, AccountMeta> = {}): FinancialAccount[] {
  return [...accounts]
    .map((a, i) => ({ a, i, order: meta[a.accountId]?.order }))
    .sort((x, y) => {
      const xo = x.order ?? Number.POSITIVE_INFINITY;
      const yo = y.order ?? Number.POSITIVE_INFINITY;
      if (xo !== yo) return xo - yo;
      if (x.order == null && y.order == null && x.a.currentBalanceCents !== y.a.currentBalanceCents) {
        return y.a.currentBalanceCents - x.a.currentBalanceCents;
      }
      return x.i - y.i;
    })
    .map((e) => e.a);
}

/** The top-level bucket of a transaction (explicit `bucket`, else derived from its subcategory). */
export function txnBucket(t: FinancialTransaction): FinancialBucket {
  return (t.bucket as FinancialBucket) ?? bucketForCategory(t.category);
}

/** Whether a transaction is a real expense outflow (excludes income + transfers by bucket). */
/** Buckets that are NOT consumption spending: income, account transfers, and debt/loan payments. */
const NON_SPEND_BUCKETS = new Set<FinancialBucket>(['Income', 'Transfer', 'Loan Payment']);
/**
 * Plaid PFC primaries that are structurally not spending. This is the AUTHORITATIVE source: our LLM/rule
 * categorizer can relabel a transfer or a card payment into a spend bucket (e.g. a Zelle payment guessed
 * as "Services"), so we exclude on Plaid's own classification regardless of the display bucket — and,
 * because `plaidPfc` is stored, this excludes already-synced transfers at rollup time without a re-sync.
 */
const NON_SPEND_PFC = new Set(['INCOME', 'TRANSFER_IN', 'TRANSFER_OUT', 'LOAN_PAYMENTS']);

/**
 * Whether a transaction is real expense outflow. Excludes income and account transfers, and ALSO loan/
 * credit-card payments (Plaid `LOAN_PAYMENTS`): paying a card off is moving money to settle debt for
 * purchases already counted on the card — counting the payment too double-counts spend (and, being on a
 * checking account with no cardholder, dumps it all into "Shared"). Excludes on Plaid's PFC first (survives
 * LLM/rule relabeling), then on the display bucket (covers Monarch/manual rows that carry no PFC).
 */
function isSpend(t: FinancialTransaction): boolean {
  if (t.amountCents <= 0) return false;
  if (t.plaidPfc && NON_SPEND_PFC.has(t.plaidPfc.toUpperCase())) return false;
  return !NON_SPEND_BUCKETS.has(txnBucket(t));
}

/** Roll spending up by the fine-grained subcategory, descending by spend. Transfers/income excluded. */
export function rollupByCategory(txns: FinancialTransaction[]): CategorySummary[] {
  const map = new Map<string, CategorySummary>();
  for (const t of txns) {
    if (!isSpend(t)) continue;
    const cur = map.get(t.category) ?? { category: t.category, spentCents: 0, count: 0 };
    cur.spentCents += t.amountCents;
    cur.count += 1;
    map.set(t.category, cur);
  }
  return [...map.values()].sort((a, b) => b.spentCents - a.spentCents);
}

/** Roll spending up by top-level bucket (the chart grouping), descending by spend. */
export function rollupByBucket(txns: FinancialTransaction[]): CategorySummary[] {
  const map = new Map<string, CategorySummary>();
  for (const t of txns) {
    if (!isSpend(t)) continue;
    const b = txnBucket(t);
    const cur = map.get(b) ?? { category: b, spentCents: 0, count: 0 };
    cur.spentCents += t.amountCents;
    cur.count += 1;
    map.set(b, cur);
  }
  return [...map.values()].sort((a, b) => b.spentCents - a.spentCents);
}

/** A bucket's spend in the current window plus the immediately-preceding window (for a delta). */
export interface WindowedCategorySummary {
  category: string;
  spentCents: number;
  /** Spend on this bucket in the prior window of equal length (for the vs-previous comparison). */
  prevSpentCents: number;
  count: number;
}

/** Rolling-window spend-by-bucket (default last 30 days) with the prior equal-length window for comparison. */
export interface SpendWindow {
  days: number;
  /** Inclusive window bounds (YYYY-MM-DD): current is (from…to], previous is (prevFrom…from]. */
  to: string;
  from: string;
  prevFrom: string;
  totalCents: number;
  prevTotalCents: number;
  buckets: WindowedCategorySummary[];
}

function isoMinusDays(iso: string, days: number): string {
  return new Date(Date.parse(iso) - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * Roll spending up by bucket over a rolling window (default 30 days ending at `today`), alongside the
 * immediately-preceding window of equal length. Buckets descend by current spend. Transfers/income
 * excluded. `today` defaults to the latest transaction date so it works on static/imported history.
 */
export function spendByBucketWindow(txns: FinancialTransaction[], today?: string, days = 30): SpendWindow {
  const to = today ?? txns.reduce((max, t) => (t.date > max ? t.date : max), '0000-00-00');
  const from = isoMinusDays(to, days);
  const prevFrom = isoMinusDays(to, days * 2);
  const cur = new Map<string, WindowedCategorySummary>();
  let totalCents = 0;
  let prevTotalCents = 0;
  for (const t of txns) {
    if (!isSpend(t)) continue;
    const b = txnBucket(t);
    if (t.date > from && t.date <= to) {
      const e = cur.get(b) ?? { category: b, spentCents: 0, prevSpentCents: 0, count: 0 };
      e.spentCents += t.amountCents;
      e.count += 1;
      cur.set(b, e);
      totalCents += t.amountCents;
    } else if (t.date > prevFrom && t.date <= from) {
      const e = cur.get(b) ?? { category: b, spentCents: 0, prevSpentCents: 0, count: 0 };
      e.prevSpentCents += t.amountCents;
      cur.set(b, e);
      prevTotalCents += t.amountCents;
    }
  }
  const buckets = [...cur.values()].sort((a, b) => b.spentCents - a.spentCents || b.prevSpentCents - a.prevSpentCents);
  return { days, to, from, prevFrom, totalCents, prevTotalCents, buckets };
}

/** Monthly outflow trend (ascending by month). Transfers/income excluded. */
export function monthlyTrend(txns: FinancialTransaction[]): TrendPoint[] {
  const map = new Map<string, number>();
  for (const t of txns) {
    if (!isSpend(t)) continue;
    const month = t.date.slice(0, 7);
    map.set(month, (map.get(month) ?? 0) + t.amountCents);
  }
  return [...map.entries()].map(([month, spentCents]) => ({ month, spentCents })).sort((a, b) => a.month.localeCompare(b.month));
}
